import React, { useEffect, useState } from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";
import { getFormFinanceSecurityOpitons, Security } from "@api";

export type FinanceFormValues = {
  isViewOnly: boolean | undefined;
  region: string;
  securities: number[];
};

type Key = keyof FinanceFormValues;

type Props = {
  application: number;
  onCancel: () => void;
  onSubmit: (values: FinanceFormValues) => void;
  open: boolean;
  title: string;
  values: Partial<FinanceFormValues>;
  readonly?: boolean;
  isResubmitForm?: boolean;
  financeFormSecuritiesForResubmit?: any;
};

export const FinanceForm: React.FC<Props> = ({
  application,
  onCancel,
  onSubmit,
  open,
  title,
  values,
  readonly,
  isResubmitForm,
  financeFormSecuritiesForResubmit,
}) => {
  const [securityOptions, setSecurityOptions] = React.useState<Security[]>([]);
  const form = Form.useManager<FinanceFormValues>({
    isViewOnly: undefined,
    region: "",
    securities: [],
    ...values,
  });

  const [securitiesForUpdate, setSecuritiesForUpdate] = useState<any>([]);
  useEffect(() => {
    if (isResubmitForm) {
      financeFormSecuritiesForResubmit(securitiesForUpdate);
    }
  }, [securitiesForUpdate]);

  React.useEffect(() => {
    getFormFinanceSecurityOpitons().then(setSecurityOptions);
  }, []);

  function onARCsFormValueChange(name: Key, value: any): void {
    if (name === "isViewOnly") {
      form.set(Form.target.VALUE, [
        name,
        value === "yes" ? true : value === "no" ? false : undefined,
      ]);
      form.set(Form.target.VALUE, ["region", ""]);
      form.set(Form.target.ERROR, ["region", undefined]);
    } else {
      form.set(Form.target.VALUE, [name, value]);
    }
    validateARCsFormValues([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function onFCCSFormValueChange(id: string, checked: boolean): void {
    const { securities } = form.values;
    const value = checked
      ? securities.concat(parseInt(id))
      : securities.filter((security) => security !== parseInt(id));

    form.set(Form.target.VALUE, ["securities", value]);
    validateARCsFormValues(["securities", value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });

    if (isResubmitForm) {
      setSecuritiesForUpdate(value);
    }
  }

  function validateARCsFormValues([name, value]: [string, any]): Promise<
    [Key, Error?]
  > {
    switch (name) {
      case "isViewOnly": {
        if (form.values.isViewOnly === undefined) {
          return Promise.resolve([
            name as Key,
            Form.Validators.hasValue(value),
          ]);
        }
        if (form.values.isViewOnly) {
          return Promise.resolve([
            name as Key,
            Form.Validators.hasValue(value),
          ]);
        }
      }
      case "region":
        return Promise.resolve([
          name as Key,
          form.values.isViewOnly ? Form.Validators.hasValue(value) : undefined,
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function validateFCCSFormValues([name, value]: [string, any]): Promise<
    [Key, Error?]
  > {
    switch (name) {
      case "securities":
        return Promise.resolve([
          name as Key,
          Form.Validators.selectValue(
            form.values.securities,
            "Please select Security"
          ),
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onFormSubmit(): void {
    switch (application) {
      case 542:
        Promise.all(
          Object.entries(form.values).map(validateARCsFormValues)
        ).then((validations) => {
          const valid = validations.reduce((valid, [name, error]) => {
            return valid && !error;
          }, true);

          if (valid) {
            onSubmit(form.values);
          } else {
            validations.forEach(([name, error]) => {
              form.set(Form.target.ERROR, [name, error]);
            });
          }
        });
        break;
      case 543:
        Promise.all(
          Object.entries(form.values).map(validateFCCSFormValues)
        ).then((validations) => {
          const valid = validations.reduce((valid, [name, error]) => {
            return valid && !error;
          }, true);

          if (valid) {
            onSubmit(form.values);
          } else {
            validations.forEach(([name, error]) => {
              form.set(Form.target.ERROR, [name, error]);
            });
          }
        });
        break;
    }
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      title={title}
      readonly={readonly}
    >
      <Form>
        {application === 542 ? (
          <React.Fragment>
            <Form.Title error={form.errors.isViewOnly}>
              1. Information
            </Form.Title>
            <Form.Radiogroup
              label="Is View Access needed?"
              name="isViewOnly"
              onChange={onARCsFormValueChange}
              options={[
                {
                  value: "yes",
                  label: "Yes",
                },
                {
                  value: "no",
                  label: "No",
                },
              ]}
              size={12}
              value={
                form.values.isViewOnly === true
                  ? "yes"
                  : form.values.isViewOnly === false
                  ? "no"
                  : "unknown"
              }
              required
              row
            />
            <Form.Dropdown
              label="Please select Region"
              onChange={onARCsFormValueChange}
              options={[
                "View Only - ALL",
                "View Only - APAC",
                "View Only - NA",
                "View Only - EUROPE",
              ]}
              name="region"
              size={12}
              value={form.values.region || ""}
              error={form.errors.region}
              required={!!form.values.isViewOnly}
              disabled={!form.values.isViewOnly}
            />
          </React.Fragment>
        ) : null}
        {application === 543 ? (
          <React.Fragment>
            {securityOptions.map(({ id, security }) => (
              <Form.Checkbox
                key={id}
                label={security}
                name={id.toString()}
                onChange={onFCCSFormValueChange}
                size={4}
                value={form.values.securities.includes(id)}
              />
            ))}
            <Form.TextError>
              {form.errors.securities?.message || ""}
            </Form.TextError>
          </React.Fragment>
        ) : null}
      </Form>
    </Wrapper>
  );
};
